/**
 * ModelFromUrl Component
 *
 * Handles loading and rendering of 3D models from URLs.
 * Supports PLY format with click interaction for measurement points.
 * Includes depth estimation calculations and expiration handling.
 */

import React, { useContext, useEffect, useState } from "react";
import {
  BufferGeometry,
  Euler,
  MeshStandardMaterial,
  NormalBufferAttributes,
  Raycaster,
  Vector3,
} from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { Html } from "@react-three/drei";

import {
  DepthEstimationStatus,
  ModelViewerContext,
} from "../../../context/model-viewer-context";
import { Card, Stack, Typography } from "@mui/material";

/**
 * Props for the ModelFromUrl component
 */
interface ModelFromUrlProps {
  /** URL of the 3D model file to load (PLY format) */
  url: string;
  /** Position offset for the model in 3D space */
  position?: Vector3;
  /** Rotation of the model */
  rotation?: Euler;
  /** Whether the model URL has expired */
  expired: boolean;
  /** Grid points for depth estimation calculations */
  gridPoints3D: Vector3[];
  /** Plane normal vector for depth calculations */
  planeNormal?: Vector3;
  /** Callback function when the model is clicked */
  onClick: (event: any) => void;
}

const ModelFromUrl: React.FC<ModelFromUrlProps> = ({
  url,
  position,
  rotation,
  expired,
  gridPoints3D,
  planeNormal,
  onClick,
}: ModelFromUrlProps) => {
  const { setIsLoading, depthStatus, setDepthStatus, setDepthEstimation } =
    useContext(ModelViewerContext);

  // State for the loaded 3D geometry
  const [geometry, setGeometry] = useState<BufferGeometry | undefined>(
    undefined
  );

  // Refs for mesh and raycasting functionality
  const meshRef = React.useRef<any>(null);
  const rayCasterRef = React.useRef<Raycaster>(new Raycaster());

  // Effect to load the 3D model from URL
  useEffect(() => {
    if (expired) {
      setGeometry(new BufferGeometry());
      setIsLoading(false);
      console.log("!!Model expired, setting geometry to empty BufferGeometry");
      return;
    }

    setIsLoading(true);
    const loader = new PLYLoader();
    loader.load(
      url,
      (loadedGeometry) => {
        setGeometry(loadedGeometry);
        setIsLoading(false);
        console.log("!!Model loaded successfully:", loadedGeometry);
      },
      undefined,
      (error) => {
        console.error("Error loading model:", error);
        setIsLoading(false);
        console.log(
          "!!Model loading failed, setting geometry to empty BufferGeometry"
        );
      }
    );
  }, [url, expired, setIsLoading]);

  // Effect to calculate depth estimation when geometry and plane normal are available
  useEffect(() => {
    if (depthStatus != DepthEstimationStatus.IN_PROGRESS || !planeNormal) {
      return;
    }

    let depthEstimation = calculateDepthEstimation(planeNormal);

    // If depth estimation is invalid, try with reversed normal
    if (depthEstimation <= 0) {
      const reverseNormal = new Vector3(
        -planeNormal.x,
        -planeNormal.y,
        -planeNormal.z
      );
      depthEstimation = calculateDepthEstimation(reverseNormal);
      console.log("!!Reversed normal depth estimation:", depthEstimation);
    }

    setDepthEstimation(depthEstimation);
    setDepthStatus(DepthEstimationStatus.COMPLETED);
  }, [depthStatus, geometry, planeNormal, setDepthEstimation, setDepthStatus]);

  /**
   * Calculates depth estimation by raycasting from grid points in the specified direction
   * @param direction - The direction vector for raycasting
   * @returns Average depth value across all grid points
   */
  const calculateDepthEstimation = (direction: Vector3): number => {
    if (!meshRef.current) {
      return 0;
    }

    if (!rayCasterRef.current) {
      return 0;
    }

    let totalDistance = 0;
    let intersectionCount = 0;

    const objectsToCheck = [meshRef.current];
    gridPoints3D.forEach((point) => {
      rayCasterRef.current.set(point, direction);
      const intersections =
        rayCasterRef.current.intersectObjects(objectsToCheck);

      if (intersections.length > 0) {
        totalDistance += intersections[0].distance;
        intersectionCount++;
      }
    });

    let averageDistance = 0;
    if (intersectionCount > 0) {
      averageDistance = totalDistance / intersectionCount;
    }

    return averageDistance;
  };

  const material = new MeshStandardMaterial({
    vertexColors: geometry?.attributes.color !== undefined,
    metalness: 0.5,
    roughness: 0.5,
  });

  const [currentTime, setCurrentTime] = React.useState<number>(0);

  const onPointerDown = (event: any) => {
    const currentTime = new Date().getTime();

    setCurrentTime(currentTime);
  };

  const onPointerUp = (event: any) => {
    const pointUpTime = new Date().getTime();

    const timeDiff = pointUpTime - currentTime;

    // don't call on click if a drag event
    if (timeDiff < 150) {
      onClick(event);
    }
  };

  return (
    <>
      <Html position={[0, 0, 0]}>
        {expired && (
          <Card>
            <Stack padding={2}>
              <Typography variant="h6">"Model has expired"</Typography>
            </Stack>
          </Card>
        )}
      </Html>
      <mesh
        ref={meshRef}
        position={position}
        rotation={rotation}
        geometry={geometry}
        scale={100}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <primitive object={material} attach="material" />
      </mesh>
      <ambientLight intensity={4} />
    </>
  );
};

export default ModelFromUrl;
